import { routeOf } from './analytics';

/**
 * Google Analytics 4.
 *
 * Второй счётчик рядом с Amplitude: продуктовую воронку читают там, а здесь
 * нужен привычный отчёт по источникам и аудитории.
 *
 * Стандартный сниппет из консоли GA сюда не вставлен дословно, и это не
 * вольность. Он шлёт `page_view` сам, а вместе с ним — полный адрес страницы.
 * У нас в адресе живёт токен приглашения (`/pair/qoespPi5…`), и это пароль к
 * чужой анкете: кто открыл ссылку, тот видит разбор пары. Уехав в GA, он
 * оказался бы в отчётах, в экспорте и у всех, кому открыт доступ к счётчику.
 *
 * Поэтому автоматический просмотр выключен (`send_page_view: false`), а
 * адреса нормализуются тем же `routeOf`, что и в Amplitude: наружу уходит
 * `/pair/:token`, а не сам токен. Остальное — тот же сниппет: та же
 * `dataLayer`, та же `gtag.js`, тот же идентификатор.
 */

/**
 * Идентификатор счётчика. Он не секрет — его видно в исходниках любой
 * страницы с GA, — поэтому лежит в коде, а не в переменных сборки: заводить
 * ради публичного значения новый build arg в compose, CI и .env.example
 * значит добавить четыре места, где он может разъехаться.
 *
 * Переменной всё же можно перебить: понадобится отдельный счётчик под стенд —
 * хватит `VITE_GA_MEASUREMENT_ID` на сборке.
 */
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? 'G-DB4BNKYF05';

/**
 * В разработке счётчик молчит — как и Amplitude. Открытая на localhost
 * страница иначе попадает в те самые отчёты по источникам, ради которых GA и
 * ставят, и «прямые заходы» распухают трафиком разработчика.
 */
const ENABLED = import.meta.env.PROD && MEASUREMENT_ID !== '';

type GtagArgs = [command: string, ...rest: unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

/**
 * Очередь `dataLayer` работает и до загрузки скрипта: gtag.js разбирает её,
 * когда доедет. Поэтому вызовы ниже безопасно делать сразу, не дожидаясь сети.
 */
function gtag(...args: GtagArgs): void {
  window.dataLayer = window.dataLayer ?? [];
  // Именно `arguments`-объект, а не массив с теми же элементами. Разбирая
  // очередь, gtag.js берёт за команду только его (`[object Arguments]`), а
  // элементы-массивы пропускает молча: скрипт грузится, `config` не
  // выполняется, хиты не уходят — в GA «данные пока не получены».
  window.dataLayer.push(argumentsOf(...args));
}

/** Те же значения, но настоящим `arguments`-объектом. */
function argumentsOf(..._args: GtagArgs): IArguments {
  // eslint-disable-next-line prefer-rest-params
  return arguments;
}

export function initGtag(): void {
  if (!ENABLED) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag = gtag;
  gtag('js', new Date());
  // Просмотр страницы шлём сами — см. комментарий к модулю.
  gtag('config', MEASUREMENT_ID, { send_page_view: false });
}

/**
 * Просмотр страницы с нормализованным адресом.
 *
 * `gtag('set')`, а не свойства одного события: значения становятся общими для
 * всех последующих, включая те, что GA собирает сам (прокрутка, уходы по
 * ссылкам). Иначе выключённый `send_page_view` спасал бы только от одного
 * события, а токен всё равно уезжал бы вместе с остальными.
 */
export function trackGtagPageView(pathname: string): void {
  if (!ENABLED) return;

  const route = routeOf(pathname);
  gtag('set', {
    page_path: route,
    // Полный адрес GA ждёт абсолютным, иначе отчёт по страницам разваливается
    // на «(not set)». Собираем его из origin и уже обезличенного пути.
    page_location: `${window.location.origin}${route}`,
    page_title: document.title,
  });
  gtag('event', 'page_view');
}
